import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, shareReplay, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { selectSalesOrganisationsWithId } from '../../../core/store/session/user-context/user-context.selectors';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../core/store';
import { Definition } from '../../../core/store/definitions/definition.interface';
import { Tile, TilesGroupedByLetter } from '../tile/tile.interface';
import { Customer } from '../../../core/store/session/user-context/user-context.interface';
import { logger } from '../../../core/util/logger';

@Injectable({ providedIn: 'root' })
export class SelectCustomerSandbox {

  protected store: Store<fromRoot.RootState> = inject(Store);

  selectedSalesOrganisationIdSubject$: Subject<string | undefined> = new BehaviorSubject(undefined);
  selectedSalesOrganisationId$ = this.selectedSalesOrganisationIdSubject$.asObservable().pipe(distinctUntilChanged(), shareReplay(1));

  salesOrganisations$ = this.store.select(selectSalesOrganisationsWithId).pipe(shareReplay(1));

  selectedSalesOrganisation$ = combineLatest([this.salesOrganisations$, this.selectedSalesOrganisationId$])
    .pipe(
      map(([salesOrganisations, selectedSalesOrganisationId]) => {
        return salesOrganisations.find(salesOrganisation => salesOrganisation.id === selectedSalesOrganisationId);
      }),
      shareReplay(1)
    );

  salesOrganisationsGroupedByLetter$ = this.salesOrganisations$.pipe(
    startWith([]),
    map(salesOrganisations => this.mapToTile(salesOrganisations, false)),
    map(tiles => this.groupByLetter(tiles || [])),
    shareReplay(1)
  );

  isEpsUser$ = this.salesOrganisations$.pipe(map(salesOrganisations => salesOrganisations?.length > 0), shareReplay(1));

  selectedCustomerIdSubject$: Subject<string | undefined> = new BehaviorSubject(undefined);
  selectedCustomerId$ = this.selectedCustomerIdSubject$.asObservable().pipe(distinctUntilChanged(), shareReplay(1));
  customersLoading$ = this.store.select(state => state.session.userContext?.customersLoading);
  customerLoading$ = this.store.select(state => state.customerInfo.loading);

  customers$: Observable<Customer[]> = this.store.select(state => state.session.userContext?.customers).pipe(shareReplay(1));
  customersGroupedByLetter$ = this.customers$.pipe(
    map(customers => this.mapToTile(customers, true)),
    map(tiles => this.groupByLetter(tiles)),
    shareReplay(1)
  );


  salesOrgTileLoading$ = combineLatest([this.customersLoading$, this.selectedSalesOrganisationId$])
    .pipe(
      map(([customersLoading, selectedSalesOrganisationId]) => customersLoading && selectedSalesOrganisationId),
      shareReplay(1)
    );

  constructor() {
    this.customerLoading$.subscribe(customerLoading => logger.log(`customerLoading: ${customerLoading}`))
    this.selectedCustomerId$.subscribe(selectedCustomerId => logger.log(`selectedCustomerId: ${selectedCustomerId}`))
    // reset selectedCustomerId when loaded
    combineLatest([this.customerLoading$, this.selectedCustomerId$])
      .pipe(debounceTime(100))
      .subscribe(([customerLoading, selectedCustomerId]) => {
      if (!customerLoading && selectedCustomerId) {
        this.selectedCustomerIdSubject$.next(undefined);
      }
    });
  }


  private mapToTile(tiles?: Definition[], copyIdToNumber = false): Tile[] {
    if (!tiles) {
      return [];
    }
    return tiles.map(tile => ({...tile, number: copyIdToNumber ? tile.id : undefined, name: tile.name || ''}));
  }

  private groupByLetter(list: Tile[]): TilesGroupedByLetter[] {
    if (!list) {
      return [];
    }

    // sort first
    list.sort((a, b) => {
      const nameASplit = a.name.split('] ')[1];
      const nameBSplit = b.name.split('] ')[1];
      const nameA = nameASplit && nameASplit.length && nameASplit.length === 2 ? nameASplit[1] : a.name;
      const nameB = nameBSplit && nameBSplit.length && nameBSplit.length === 2 ? nameBSplit[1] : b.name;

      return nameA.localeCompare(nameB);
    });

    return list.reduce((res, c) => {
      const nameSplit = c.name.split('] ');
      const name = nameSplit && nameSplit.length && nameSplit.length === 2 ? nameSplit[1] : c.name;
      const firstChar = name[0].toLocaleUpperCase();
      let entry = res.find(e => e.firstLetter === firstChar);
      if (!entry) {
        entry = { firstLetter: firstChar, tiles: [] };
        res.push(entry);
      }
      entry.tiles.push(c);
      return res;
    }, []);
  }
}
